import type { SxProps, Theme } from "@mui/material/styles";

import { MID, NOT_WIDE, TINY, WIDE, marketingPalette } from "./tokens";

/**
 * `.h2_new` — every section heading outside the blog and the academy. Not the
 * shell theme's `h2`, which is the blog's and runs 36/43.2 stepping to 26/31.2.
 * Steps are declared widest-first so the narrowest rule wins on insertion
 * order, which is how emitted media queries resolve.
 */
export const HEADING_2 = {
  color: marketingPalette.ink,
  fontSize: 36,
  lineHeight: "48px",
  fontWeight: 500,
  letterSpacing: "-0.04em",
  textWrap: "balance",
  [NOT_WIDE]: { lineHeight: "40px" },
  [TINY]: { fontSize: 27, lineHeight: "35.1px" },
} satisfies SxProps<Theme>;

/**
 * `.h2_new` on an `h3`, as the paid-search pages set their panels' and
 * sections' titles: the `h3`'s own 40 line from the wide step too, where on
 * an `h2` it is 48.
 */
export const HEADING_2_ON_H3 = {
  ...HEADING_2,
  [WIDE]: { lineHeight: "40px" },
} satisfies SxProps<Theme>;

/** `.h3_new` on an `h3`: a card's or a panel's heading, the element's own tracking with it. */
export const HEADING_3 = {
  color: marketingPalette.ink,
  fontSize: 32,
  lineHeight: "41.6px",
  fontWeight: 500,
  letterSpacing: "-0.04em",
  [TINY]: { fontSize: 24, lineHeight: "32px" },
} satisfies SxProps<Theme>;

/**
 * `.body_new`: the sub-copy under a hero, and the size every card title takes.
 * It follows its section's alignment: centred under a centred heading, left
 * in cards and left-aligned sections.
 */
export const BODY = {
  color: marketingPalette.ink,
  fontSize: 17,
  lineHeight: "25.5px",
  [MID]: { fontSize: 16, lineHeight: "24px" },
  [TINY]: { fontSize: 15, lineHeight: "21px" },
} satisfies SxProps<Theme>;

/** `.body_new.is-small`: the quieter line under a section heading, aligned as `BODY` is. */
export const BODY_SMALL = {
  color: marketingPalette.inkSubtle,
  fontSize: 15,
  lineHeight: "23.25px",
  [TINY]: { fontSize: 14, lineHeight: "21.7px" },
} satisfies SxProps<Theme>;

/**
 * `.b-card_title` — the title on a card inside a grid, a step under a section
 * heading and in the muted ink rather than the page's own. A section's own
 * single card takes the larger card-heading size instead.
 */
export const CARD_TITLE = {
  color: marketingPalette.inkMuted,
  fontSize: 18.08,
  lineHeight: "23.504px",
  fontWeight: 500,
  letterSpacing: "-0.5424px",
} satisfies SxProps<Theme>;
